import React from "react";
import { connect } from "react-redux";
import VideoComponent from "./video/VideoComponent";
import ImageComponent from "./image/ImageComponent";
import { logEvent, requestPlaylistPosition, routeSelected, setCurrentTab, setUserPreference } from "redux/actions/index";
import { absoluteTimeLookup, getAbsoluteTimestamp, imageToVideoIndex, videoTimestampExtractor, videoToImageIndex } from "../util/PlaylistUtils";
import _ from "lodash";
import OBCSpinner from "../util/OBC";
import RailInspectBigScroller from "./image/RailInspectBigScroller";
import RailInspectControls from "./image/RailInspectControls";
import { Link } from "react-router-dom";
import { playlistDistanceToTimestamp } from "../util/GeometryUtilsV2";
import { sourceOffsetSelector } from "redux/selectors";
class VideoOrImageComponent extends React.PureComponent {
    static defaultProps = {
        viewMode: "default",
    };

    constructor(props) {
        super(props);

        this.state = {
            railIndex: 0,
            sources: [],
            archivedSources: 0,
        };
        this.loadingSessionLabel = React.createRef();
    }

    componentDidMount() {
        this.updateSourcesAndSelect();
    }

    componentDidUpdate(prevProps, prevState) {
        if (_.isEmpty(_.get(this.props.playlist, ["route_locations_v2", "time_to_distance"]))) {
            // if loadingSessionLabel is rendered focus on it so screen reader if triggered.
            if (this.loadingSessionLabel && this.loadingSessionLabel.current) {
                this.loadingSessionLabel.current.focus();
            }
        }

        if (
            this.props.playlist.loaded !== prevProps.playlist.loaded ||
            this.props.playlist.routeID !== prevProps.playlist.routeID ||
            this.props.streamInfo !== prevProps.streamInfo
        ) {
            this.updateSourcesAndSelect();
        }
    }

    setNewRailIndex = (newIndex) => {
        this.setState({
            railIndex: newIndex,
        });
    };

    updateSourcesAndSelect = () => {
        const allSources = this.getSourceList();
        const archivedCount = this.props.streamInfo.filter((s) => s.archived).length;

        const sources = allSources.filter((source) => {
            const stream = _.get(this.props.streamInfo, [source.index], {});
            return !stream.archived;
        });

        this.setState({ sources, archivedSources: archivedCount }, () => {
            const currentArchived = _.get(this.props.streamInfo, [this.props.sourceIndex, "archived"]);

            if (currentArchived) {
                const newIndex = _.findIndex(this.props.streamInfo, { archived: 0 });

                if (newIndex > -1) {
                    this.selectStream({ target: { value: `${newIndex}.0` } });
                }
            }
        });
    };

    getSourceList = () => {
        const sources = [];
        const playlist = this.props.playlist;

        if (!playlist) return sources;

        // Video sources
        if (playlist.video && playlist.video.length) {
            playlist.video.forEach((videoItem, i) => {
                if (!videoItem.length) return;

                const sourceStreamData = _.get(this.props.streamInfo, [i], {});
                const sourceStreamInfo = _.get(sourceStreamData, "info", {});
                const sourceName = _.get(sourceStreamInfo, "type", `Camera ${i + 1}`);
                const sourceLabel = _.get(sourceStreamInfo, "label", "");
                const has8K = sourceLabel.toLowerCase().includes("8k");

                let name = sourceName;
                if (sourceLabel) {
                    name += ` (${sourceLabel})`;
                }

                const streams = [
                    {
                        name: "Video",
                        videoIndex: i,
                        isVideo: true,
                        isEnhanced: false,
                        isStills: false,
                        disabled: has8K,
                    },
                ];

                if (this.props.session.first_seen > 1631710005) {
                    streams.push({
                        name: "Low Bandwidth",
                        videoIndex: i,
                        isVideo: true,
                        isEnhanced: "low_res",
                        isStills: false,
                    });
                }

                streams.push({
                    name: "Still Images",
                    videoIndex: i,
                    isVideo: true,
                    isEnhanced: false,
                    isStills: true,
                });

                sources.push({ name, index: i, streams });
            });
        }

        // Image sources
        if (playlist.image && playlist.image.length) {
            sources.push({
                name: `Camera ${sources.length + 1}`,
                index: 0,
                streams: [
                    {
                        name: "Stills",
                        videoIndex: 0,
                        isVideo: false,
                        isEnhanced: false,
                        isStills: false,
                    },
                ],
            });
        }

        return sources;
    };

    selectStream = (stream) => {
        if (stream && stream.target) {
            let streamReference = stream.target.value.split(".");
            let sourceIndex = 1 * streamReference[0];
            let streamIndex = streamReference[1];
            for (let i in this.state.sources) {
                let source = this.state.sources[i];
                for (let j in source.streams) {
                    let _stream = source.streams[j];
                    if (_stream.videoIndex === sourceIndex && j === streamIndex) {
                        stream = _stream;
                        break;
                    }
                }
            }
        }

        let newIndex = 0;
        let newOffset = 0;

        console.log("New stream selected: ", stream);

        if (this.props.isVideo) {
            //Switching from video
            if (stream.isVideo) {
                //Switching to video
                const currentPlaylist = this.props.playlist.video[this.props.sourceIndex];
                let currentPlaylistItem = currentPlaylist[this.props.index];
                if (!currentPlaylistItem) {
                    currentPlaylistItem = currentPlaylist[0];
                }

                // Get geometry and offsets
                const geometry = this.props.playlist.route_locations_v2;
                const currentSourceOffset = sourceOffsetSelector({ playlist: this.props.fullPlaylist, sessions: this.props.sessions });
                const targetSourceOffset = sourceOffsetSelector({ playlist: this.props.fullPlaylist, sessions: this.props.sessions }, stream.videoIndex);
                let newTimestamp = videoTimestampExtractor(currentPlaylistItem); // Fallback in case playlistDistance is not available

                const offsetDiff = currentSourceOffset - targetSourceOffset;
                // Try to use playlistDistanceToTimestamp to get the timestamp for the target camera
                if (geometry && this.props.playlistDistance) {
                    const adjustedDistance = this.props.playlistDistance + offsetDiff;
                    newTimestamp = playlistDistanceToTimestamp(adjustedDistance, geometry);
                }

                // Find the closest index in the new playlist for the calculated timestamp
                const newPlaylist = this.props.playlist.video[stream.videoIndex];
                newIndex = absoluteTimeLookup(newTimestamp, newPlaylist);

                if (newIndex !== -1) {
                    const newAbsoluteTimestamp = videoTimestampExtractor(newPlaylist[newIndex]);
                    newOffset = newTimestamp - newAbsoluteTimestamp;
                }
                if (newOffset < 0) {
                    newOffset = 0;
                }
            } else {
                //Switching to images
                newIndex = !_.isNil(this.props.playlist.video)
                    ? videoToImageIndex(this.props.index, this.props.playlist.video[this.props.sourceIndex], this.props.playlist.image)
                    : -1;
            }
        } else {
            //Switching from images
            if (stream.isVideo) {
                //Switching to video
                newIndex = imageToVideoIndex(this.props.index, this.props.playlist.video[stream.videoIndex], this.props.playlist.image);
            } else {
                //Switching to images (shouldn't really happen, because there's only one image source available)
                newIndex = this.props.index || 0;
            }
        }

        if (newIndex === -1) {
            newIndex = 0;
            newOffset = 0;
        }

        if (this.props.isVideo && !stream.isVideo) {
            this.props.dispatch(logEvent("Video", "Switch to Images"));
        } else if (!this.props.isVideo && stream.isVideo) {
            this.props.dispatch(logEvent("Images", "Switch to Video"));
        } else {
            if (this.props.sourceIndex !== stream.videoIndex) {
                this.props.dispatch(logEvent("Video", "Select Source", stream.videoIndex));
            }
            if (this.props.isStills !== stream.isStills) {
                this.props.dispatch(logEvent("Video", "Toggle Stills Only", stream.isStills));
            }
        }

        if (this.props.isEnhanced !== stream.isEnhanced) {
            this.props.dispatch(logEvent("Video", "Toggle Enhanced", stream.isEnhanced));
        }

        if (stream.isVideo) {
            this.props.dispatch(setUserPreference("defaultStills", stream.isStills));
            this.props.dispatch(setUserPreference("defaultEnhanced", stream.isEnhanced));
        }

        console.log("Switching to source: ", stream, "at position and offset:", newIndex, newOffset);
        this.props.dispatch(requestPlaylistPosition(stream.isVideo, stream.isEnhanced, stream.isStills, stream.videoIndex, newIndex, newOffset));
    };

    imageIndexChanged = (newIndex) => {
        // this.props.dispatch(selectBookmark(null));
        this.props.dispatch(requestPlaylistPosition(true, false, true, 0, newIndex, 0));
    };

    render() {
        let content = (
            <div className={`Center-Content Info-Message ${this.props.isWidget ? "Info-Message-Widget" : ""}`}>
                <div>{this.props.infoMessage ?? "Please select a session from the map or list below."}</div>
            </div>
        );

        if (this.props.isPointOnTheMapClicked) {
            content = (
                <div className={`Center-Content Info-Message ${this.props.isWidget ? "Info-Message-Widget" : ""}`}>
                    <OBCSpinner colorScheme="mono" />
                    <div
                        className="HiddenFocus"
                        ref={this.loadingSessionLabel}
                        tabIndex={0}>
                        Loading session data, please wait...
                    </div>
                </div>
            );
        } else if (this.props.playlist && this.props.playlist.mpdURLs && this.props.playlist.routeID) {
            let hasVideo = false;
            this.props.streamInfo.forEach((stream) => {
                if (!stream.archived && _.get(this.props.playlist, ["video", stream.stream_index, "length"])) {
                    hasVideo = true;
                }
            });
            let hasImages = _.get(this.props.playlist, ["image", "length"], false);

            if (this.props.playlist && (hasVideo || hasImages)) {
                if (this.props.isVideo) {
                    content = (
                        <VideoComponent
                            sources={this.state.sources}
                            selectStream={this.selectStream}
                            viewMode={this.props.viewMode}
                            playerControls={_.isUndefined(this.props.playerControls) ? true : this.props.playerControls}
                        />
                    );
                } else {
                    content = (
                        <ImageComponent
                            playerControls={true}
                            sources={this.state.sources}
                            selectStream={this.selectStream}
                            viewMode={this.props.viewMode}
                        />
                    );
                }
            } else if (!this.props.playlist.loaded) {
                content = (
                    <div className={`Center-Content Info-Message ${this.props.isWidget ? "Info-Message-Widget" : ""}`}>
                        <OBCSpinner colorScheme="mono" />
                        <div
                            className="HiddenFocus"
                            ref={this.loadingSessionLabel}
                            tabIndex={0}>
                            Loading session data, please wait...
                        </div>
                    </div>
                );
            } else if (this.props.playlist.loaded) {
                content = (
                    <div
                        id="intro-tour-rail-inspection"
                        className={"VideoContainer"}
                        style={{ minHeight: "40vh" }}>
                        <div className="VideoContainer__Button">
                            <button
                                onClick={() => {
                                    this.props.dispatch(setCurrentTab("sessions"));
                                    this.props.dispatch(routeSelected(null));
                                }}>
                                Close Session
                            </button>
                            {this.props.hasRailInspectionImagesData ? (
                                <Link
                                    to={`/rail-inspection/${this.props.playlist.routeID}${this.props.absolutePlaylistTimestamp ? "/" + this.props.absolutePlaylistTimestamp : ""}`}>
                                    <button id="intro-tour-image-fullscreen">Open Inspection Interface</button>
                                </Link>
                            ) : null}
                        </div>
                        <div className="VideoContainer__Button Left"></div>
                        <RailInspectBigScroller
                            inPlayer
                            videoless
                            newRailIndex={this.state.railIndex}
                            imageIndexChanged={this.setNewRailIndex}
                        />
                        <RailInspectControls
                            extraStyles={{
                                position: "absolute",
                                zIndex: 400,
                                height: 55,
                            }}
                            playing={false}
                            // imageIndexChanged={this.imageIndexChanged}
                            videoless={true}
                            imageIndexChanged={this.setNewRailIndex}
                            newRailIndex={this.state.railIndex}
                            inPlayer
                        />
                    </div>
                );
            }
        }

        return content;
    }
}

const mapStateToProps = ({ playlist, dashboards, access_token, sessions, railInspection }) => {
    let dashboard = _.find(dashboards, { access_token: access_token });

    const isWidget = ["widget_layout", "minimal_layout"].includes(dashboard?.workspace_layout);

    const currentSession = _.get(sessions, [playlist.data.routeID], {});
    let streamInfo = _.get(currentSession, ["stream_info"], []);
    const hasRailInspectionImagesData = !_.isEmpty(_.get(railInspection.railInspectionImages, "data", []));

    let absolutePlaylistTimestamp = getAbsoluteTimestamp(playlist) * 1000;
    if (!absolutePlaylistTimestamp && railInspection.railInspectionImages.data?.length && !playlist.video?.length) {
        const currentIndex = _.get(playlist, ["position", "currentIndex"]);
        if (currentIndex) {
            absolutePlaylistTimestamp = _.get(railInspection.railInspectionImages.data, [currentIndex, "timestamp"], 0);
        }
    }

    return {
        playlist: playlist.data,
        isVideo: playlist.position.isVideo,
        isEnhanced: playlist.position.isEnhanced,
        isStills: playlist.position.isStills,
        isPointOnTheMapClicked: playlist.position.pointOnTheMapClicked,
        sourceIndex: playlist.position.sourceIndex,
        index: playlist.position.currentIndex,
        timeOffset: playlist.position.currentTimeOffset,
        isBetaDashboard: dashboard && dashboard.isBeta,
        streamInfo,
        session: currentSession,
        absolutePlaylistTimestamp,
        isWidget,
        hasRailInspectionImagesData,
        playlistLoaded: playlist.data.loaded && !_.isEmpty(_.get(playlist.data, ["route_locations_v2", "time_to_distance"])),
        playlistDistance: playlist.position.playlistDistance,
        sessions,
        fullPlaylist: playlist,
    };
};

export default connect(mapStateToProps)(VideoOrImageComponent);
